/**
 * この実装はcross-storageほぼそのままです。
 */
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;

    return v.toString(16);
  });
};
export class StorageClient {
  id: string;
  frameId: string;
  origin: string;
  requests: Record<string, any>;
  connected: boolean;
  closed: boolean;
  count: number;
  timeout: number;
  hub: null | Window;

  constructor(url: string, opts?: { frameId?: string; timeout?: number }) {
    opts = opts || {};

    this.id = generateUUID();
    this.frameId = opts.frameId || "CrossStorageClient-" + this.id;
    this.origin = new URL(url).origin;
    this.requests = {};
    this.connected = false;
    this.closed = false;
    this.count = 0;
    this.timeout = opts.timeout || 5000;

    window.addEventListener("message", this.listener.bind(this), false);

    const frame = this.createFrame(url);
    this.hub = frame.contentWindow;
  }

  onConnect() {
    if (this.connected) {
      return Promise.resolve();
    } else if (this.closed) {
      return Promise.reject(new Error("StorageClient has closed."));
    }

    if (!this.requests.connect) {
      this.requests.connect = [];
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(function () {
        reject(new Error("StorageClient could not connect."));
      }, this.timeout);

      this.requests.connect.push((err: string) => {
        clearTimeout(timeout);
        if (err) return reject(err);

        resolve(undefined);
      });
    });
  }

  set(key: string, value: string) {
    return this.request("set", {
      key: key,
      value: value,
    });
  }

  get(...keys: string[]) {
    return this.request("get", { keys });
  }

  del(...keys: string[]) {
    return this.request("del", { keys });
  }

  clear() {
    return this.request("clear");
  }

  getKeys() {
    this.request("getKeys");
  }

  close() {
    const frame = document.getElementById(this.frameId);
    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }

    window.removeEventListener("message", this.listener, false);

    this.connected = false;
    this.closed = true;
  }

  private listener(message: MessageEvent) {
    let i, error, response;

    if (this.closed || !message.data || typeof message.data !== "string") {
      return;
    }

    const origin = message.origin;

    if (origin !== this.origin) return;

    if (message.data === "cross-storage:unavailable") {
      if (!this.closed) this.close();
      if (!this.requests.connect) return;

      error = new Error(
        "Closing StorageClient. Could not access localStorage in StorageHub.",
      );
      for (i = 0; i < this.requests.connect.length; i++) {
        this.requests.connect[i](error);
      }

      return;
    }

    if (message.data.indexOf("cross-storage:") !== -1 && !this.connected) {
      this.connected = true;
      if (!this.requests.connect) return;

      for (i = 0; i < this.requests.connect.length; i++) {
        this.requests.connect[i](error);
      }
      delete this.requests.connect;
    }

    if (message.data === "cross-storage:ready") return;

    try {
      response = JSON.parse(message.data);
    } catch (e) {
      return;
    }

    if (!response.id) return;

    if (this.requests[response.id]) {
      this.requests[response.id](response.error, response.result);
    }
  }

  private createFrame(url: string) {
    const frame = window.document.createElement("iframe");
    frame.id = this.frameId;

    frame.style.display = "none";
    frame.style.position = "absolute";
    frame.style.top = "-999px";
    frame.style.left = "-999px";

    window.document.body.appendChild(frame);
    frame.src = url;

    return frame;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private request(method: string, params?: {}) {
    if (this.closed) {
      return Promise.reject(new Error("StorageClient has closed."));
    }

    this.count++;

    const req = {
      id: this.id + ":" + this.count,
      method: "cross-storage:" + method,
      params: params,
    };

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        if (!this.requests[req.id]) return;

        delete this.requests[req.id];
        reject(
          new Error("Timeout: StorageClient could not perform " + req.method),
        );
      }, this.timeout);

      this.requests[req.id] = (err: string, result: string) => {
        clearTimeout(timeout);
        delete this.requests[req.id];
        if (err) return reject(new Error(err));
        resolve(result);
      };

      if (this.hub) {
        this.hub.postMessage(JSON.stringify(req), this.origin);
      } else {
        reject(new Error("Not found StorageHub."));
      }
    });
  }
}
